import axios from 'axios';
import { axiosETAGCache } from 'axios-etag-cache';

// Apply the axios ETAG interceptor
const axiosWithETAGCache = axiosETAGCache(axios);
const api = {
  // ============================================================================
  // Default Outbound Rules
  // ============================================================================
  async getDefaultOutboundRules() {
    const queryParams = {};

    const response = await axiosWithETAGCache.get(`/default_outbound_rules`, {
      params: queryParams,
    });

    return response.data;
  },

  async saveDefaultOutboundRules(defaultOutboundRules) {
    const response = await axios.put(
      `/default_outbound_rules`,
      defaultOutboundRules
    );
    return response.data;
  },

  // ============================================================================
  // Campaign CRUD Operations
  // ============================================================================

  /**
   * Get list of campaigns with pagination and filtering
   * @param {Object} tableQuery - Query parameters for pagination/filtering
   * @returns {Promise<Array>} - List of campaigns
   */
  async getCampaigns(tableQuery) {
    const response = await axiosWithETAGCache.get('/campaign', {
      params: { query: JSON.stringify(tableQuery) },
    });
    return response.data;
  },

  /**
   * Get a single campaign by ID
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} - The campaign object
   */
  async getCampaign(campaignId) {
    const response = await axiosWithETAGCache.get(`/campaign/${campaignId}`);
    return response.data;
  },

  /**
   * Get the JSON schema for Campaign model
   * @returns {Promise<Object>} - The JSON schema
   */
  async getCampaignSchema() {
    const response = await axiosWithETAGCache.get('/campaign/schema');
    return response.data;
  },

  /**
   * Create a new campaign
   * @param {Object} campaignData - The campaign data
   * @returns {Promise<Object>} - The created campaign
   */
  async createCampaign(campaignData) {
    const response = await axios.post('/campaign', campaignData);
    return response.data;
  },

  /**
   * Update an existing campaign
   * @param {Object} campaign - The campaign data (must include id)
   * @returns {Promise<Object>} - The updated campaign
   */
  async updateCampaign(campaign) {
    const response = await axios.put(`/campaign/${campaign.id}`, campaign);
    return response.data;
  },

  /**
   * Delete a campaign
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteCampaign(campaignId) {
    const response = await axios.delete(`/campaign/${campaignId}`);
    return response.data;
  },

  // ============================================================================
  // Campaign Status Actions
  // ============================================================================

  /**
   * Start or resume a campaign
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} - The updated campaign
   */
  async startCampaign(campaignId) {
    const response = await axios.post(`/campaign/${campaignId}/start`);
    return response.data;
  },

  /**
   * Pause a running campaign
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} - The updated campaign
   */
  async pauseCampaign(campaignId) {
    const response = await axios.post(`/campaign/${campaignId}/pause`);
    return response.data;
  },

  /**
   * Cancel a campaign
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} - The updated campaign
   */
  async cancelCampaign(campaignId) {
    const response = await axios.post(`/campaign/${campaignId}/cancel`);
    return response.data;
  },

  // ============================================================================
  // Campaign Contact Plans (Call Queue)
  // ============================================================================

  /**
   * Get contact plans (call queue) for a campaign
   * @param {string} campaignId - The campaign ID
   * @param {Object} options - Query options
   * @param {string} options.status - Optional status filter (queued, scheduled, in_progress, completed, failed, cancelled, skipped)
   * @param {number} options.limit - Maximum results (default: 100, max: 1000)
   * @param {number} options.offset - Results to skip (default: 0)
   * @returns {Promise<Object>} - Contact plans with pagination and stats
   */
  async getContactPlans(campaignId, options = {}) {
    const params = {};
    if (options.status) params.status = options.status;
    if (options.limit) params.limit = options.limit;
    if (options.offset) params.offset = options.offset;

    const response = await axiosWithETAGCache.get(
      `/campaign/${campaignId}/call-plan`,
      { params }
    );
    return response.data;
  },

  /**
   * Get aggregated statistics for a campaign's contact plans
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} - Stats by status
   */
  async getContactPlanStats(campaignId) {
    const response = await axiosWithETAGCache.get(
      `/campaign/${campaignId}/call-plan/stats`
    );
    return response.data;
  },

  /**
   * Get a single contact plan by ID
   * @param {string} campaignId - The campaign ID
   * @param {string} planId - The contact plan ID
   * @returns {Promise<Object>} - The contact plan
   */
  async getContactPlan(campaignId, planId) {
    const response = await axiosWithETAGCache.get(
      `/campaign/${campaignId}/call-plan/${planId}`
    );
    return response.data;
  },
};

export default api;
